import type { Object3D } from 'three';
import {
  ImportedModelObjectMetadataService,
  type ModelAssetNode,
  type ProjectModelLoadResult,
} from '@puppycad/cad-core';

/**
 * 项目模型场景对象反查输入。
 */
export interface ResolveProjectModelNodeBySceneObjectIdsInput {
  /** 当前项目模型加载结果。 */
  readonly loadResult: ProjectModelLoadResult;
  /** 候选场景对象 UUID。 */
  readonly objectIds: readonly string[];
  /** 按 UUID 读取当前场景对象。 */
  readonly getSceneObjectById?: (objectId: string) => Object3D | null | undefined;
  /**
   * 设计树动作携带的稳定身份。
   *
   * 该值可能是 canonical nodeKey，也可能是场景载体写入的 runtimeAssemblyNodeId。
   */
  readonly stableNodeKey?: string;
}

/**
 * 在项目模型树中按 nodeKey 查找节点。
 *
 * @param node 当前模型节点。
 * @param nodeKey 目标 nodeKey。
 * @returns 命中的模型节点；未命中时返回 null。
 */
function findProjectModelNodeByNodeKey(node: ModelAssetNode, nodeKey: string): ModelAssetNode | null {
  if (node.nodeKey === nodeKey) {
    return node;
  }

  for (const child of node.children) {
    const matchedNode = findProjectModelNodeByNodeKey(child, nodeKey);
    if (matchedNode) {
      return matchedNode;
    }
  }

  return null;
}

/**
 * 按绑定表中的 objectId 定位项目模型节点。
 *
 * @param loadResult 当前项目模型加载结果。
 * @param objectId 场景对象 UUID。
 * @returns 命中的模型节点；未命中时返回 null。
 */
function resolveProjectModelNodeByBoundObjectId(
  loadResult: ProjectModelLoadResult,
  objectId: string,
): ModelAssetNode | null {
  const binding = loadResult.sceneBindings.find((sceneBinding) => sceneBinding.objectId === objectId);

  return binding ? findProjectModelNodeByNodeKey(loadResult.root, binding.nodeKey) : null;
}

/**
 * 按运行时装配身份反查 canonical 项目节点。
 *
 * @param loadResult 当前项目模型加载结果。
 * @param runtimeAssemblyNodeId 场景载体携带的运行时装配身份。
 * @returns 唯一命中的 canonical 节点；缺失或歧义时返回 null。
 */
function resolveProjectModelNodeByRuntimeAssemblyNodeId(
  loadResult: ProjectModelLoadResult,
  runtimeAssemblyNodeId: string,
): ModelAssetNode | null {
  const matchedBindings = Array.from(loadResult.runtimeState?.bindingsByNodeKey.values() ?? [])
    .filter((binding) => binding.runtimeAssemblyNodeId === runtimeAssemblyNodeId);

  /**
   * 稳定身份必须唯一。这里禁止继续按对象名或场景父级猜测，避免一级节点动作误命中根装配体。
   */
  if (matchedBindings.length !== 1) {
    return null;
  }

  return findProjectModelNodeByNodeKey(loadResult.root, matchedBindings[0].nodeKey);
}

/**
 * 读取对象自身或父级 metadata 中的 stable 装配节点。
 *
 * @param root 当前项目模型根。
 * @param object 当前候选对象。
 * @returns 命中的模型节点；metadata 不属于当前项目模型时返回 null。
 */
function resolveProjectModelNodeByObjectMetadata(
  root: ModelAssetNode,
  object: Object3D,
): ModelAssetNode | null {
  let currentObject: Object3D | null = object;

  while (currentObject) {
    const metadata = ImportedModelObjectMetadataService.readOwnMetadata(currentObject);
    const assemblyNodeId = (
      metadata?.selectionTarget?.assemblyNodeId
      ?? metadata?.assemblyNodeId
      ?? ''
    ).trim();

    if (assemblyNodeId) {
      const node = findProjectModelNodeByNodeKey(root, assemblyNodeId);
      if (node) {
        return node;
      }
    }

    currentObject = currentObject.parent;
  }

  return null;
}

/**
 * 按对象 metadata 中的选择代理继续反查绑定节点。
 *
 * 设计说明：右侧设计树经常把 selectionObjectId 作为动作 objectId，
 * 但切换后的 stable direct child 绑定可能落在选择代理上游根对象。
 *
 * @param input 当前加载结果、候选对象和对象查找器。
 * @returns 命中的模型节点；代理缺失或未绑定时返回 null。
 */
function resolveProjectModelNodeBySelectionProxy(input: {
  readonly loadResult: ProjectModelLoadResult;
  readonly object: Object3D;
  readonly getSceneObjectById?: (objectId: string) => Object3D | null | undefined;
}): ModelAssetNode | null {
  const selectionProxyObjectId = ImportedModelObjectMetadataService.readOwnMetadata(input.object)?.selectionProxyObjectId
    ?.trim();

  if (!selectionProxyObjectId || selectionProxyObjectId === input.object.uuid) {
    return null;
  }

  return resolveProjectModelNodeByBoundObjectId(input.loadResult, selectionProxyObjectId)
    ?? resolveProjectModelNodeByObjectMetadata(
      input.loadResult.root,
      input.getSceneObjectById?.(selectionProxyObjectId) ?? input.object,
    );
}

/**
 * 从场景对象本身沿父级链路反查项目模型节点。
 *
 * @param input 当前加载结果、起始对象和对象查找器。
 * @returns 命中的模型节点；未命中时返回 null。
 */
function resolveProjectModelNodeBySceneObject(input: {
  readonly loadResult: ProjectModelLoadResult;
  readonly object: Object3D;
  readonly getSceneObjectById?: (objectId: string) => Object3D | null | undefined;
}): ModelAssetNode | null {
  let currentObject: Object3D | null = input.object;

  while (currentObject) {
    const boundNode = resolveProjectModelNodeByBoundObjectId(input.loadResult, currentObject.uuid);
    if (boundNode) {
      return boundNode;
    }

    const metadataNode = resolveProjectModelNodeByObjectMetadata(input.loadResult.root, currentObject);
    if (metadataNode) {
      return metadataNode;
    }

    const proxyNode = resolveProjectModelNodeBySelectionProxy({
      loadResult: input.loadResult,
      object: currentObject,
      getSceneObjectById: input.getSceneObjectById,
    });
    if (proxyNode) {
      return proxyNode;
    }

    currentObject = currentObject.parent;
  }

  return null;
}

/**
 * 根据场景对象 ID 定位项目模型节点。
 *
 * @param input 当前加载结果与候选对象 ID。
 * @returns 命中的模型节点；未命中时返回 null。
 */
export function resolveProjectModelNodeBySceneObjectIds(
  input: ResolveProjectModelNodeBySceneObjectIdsInput,
): ModelAssetNode | null {
  const stableNodeKey = input.stableNodeKey?.trim() ?? '';
  if (stableNodeKey) {
    /**
     * 设计树已经提供稳定身份时只允许走身份图：先匹配 canonical nodeKey，再匹配运行时 occurrence。
     * 两者都失败即返回 null，不能用场景对象回退覆盖身份错误。
     */
    return findProjectModelNodeByNodeKey(input.loadResult.root, stableNodeKey)
      ?? resolveProjectModelNodeByRuntimeAssemblyNodeId(input.loadResult, stableNodeKey);
  }

  const normalizedObjectIds = input.objectIds
    .map((objectId) => objectId.trim())
    .filter((objectId) => objectId.length > 0);

  for (const objectId of normalizedObjectIds) {
    const boundNode = resolveProjectModelNodeByBoundObjectId(input.loadResult, objectId);
    if (boundNode) {
      return boundNode;
    }
  }

  for (const objectId of normalizedObjectIds) {
    const sceneObject = input.getSceneObjectById?.(objectId) ?? null;
    if (!sceneObject) {
      continue;
    }

    const sceneObjectNode = resolveProjectModelNodeBySceneObject({
      loadResult: input.loadResult,
      object: sceneObject,
      getSceneObjectById: input.getSceneObjectById,
    });

    if (sceneObjectNode) {
      return sceneObjectNode;
    }
  }

  return null;
}
